import { SESSION_STATUS } from "./constants/appointmentSessionConstants";
import uuidv4 from "uuid/v4";
import moment from "moment/moment";
import { forEach } from 'lodash';
import { getSessionSlotDuration } from "../components/utils/sessionUtilities";
import {getIntervalRrule, modifyRruleString} from '../components/utils/rruleUtils';

const getAppointmentSessionStatus = (sessObj) => {
  if (sessObj.active) return SESSION_STATUS?.ACTIVE;
  if (sessObj.locked) return SESSION_STATUS?.LOCKED;

  return SESSION_STATUS?.CANCELLED;
};

export const getDateWithHours = (date, hour) => moment(date).set({hours: moment(hour).hours(), minute: moment(hour).minute, seconds: 0, millisecond: 0});

const cleanSessionObj = (sessionObj) => {
    const newObj = {
        assignedSpecialty: sessionObj.assignedSpecialty,
        clinicsId: sessionObj.clinicsId,
        startDate: sessionObj.startDate,
        endDate: sessionObj.endDate,
        hospitalId: sessionObj.hospitalId,
        wardId: sessionObj.wardId,
        practitioner: sessionObj.practitioner,
        practitionerRole: sessionObj.practitionerRole,
        sessionName: sessionObj.sessionName.trim(),
        slotDurationInMinutes: sessionObj.slotDurationInMinutes,
        status: sessionObj.status,
        rrule: sessionObj.rrule,
        slotStartTime: sessionObj.slotStartTime,
    };

    if (sessionObj.healthCareService) {
        newObj.healthCareService = sessionObj.healthCareService;
    }

    return newObj;
}

export const createAppointmentSessionObject = (sessionState) => {
  return {
    sessionId: sessionState?.sessionId || sessionState?.id || uuidv4(),
    startDate: moment(sessionState.startDate)
      .set({
        hours: sessionState.startTime.hours(),
        minutes: sessionState.startTime.minutes(),
        seconds: 0,
        miliseconds: 0,
      })
      // .toISOString(),
      .toDate(),
    endDate: moment(sessionState.endDate)
      .set({
        hours: sessionState.endTime.hours(),
        minutes: sessionState.endTime.minutes(),
        seconds: 0,
        miliseconds: 0,
      })
      // .toISOString(),
      .toDate(),
    wardId: sessionState.wardId,
    hospitalId: sessionState.hospitalId,
    sessionName: sessionState.sessionName.trim(),
    clinicsId: sessionState.clinicsId ?? "",
    practitioner: sessionState.practitioner,
    practitionerRole: sessionState.practitionerRole,
    assignedSpecialty: sessionState.assignedSpecialty,
    status: getAppointmentSessionStatus(sessionState),
    rrule: getIntervalRrule(sessionState).toString(),
    slotStartTime: moment(sessionState.startTime).format("HH:mm"),
    slotDurationInMinutes: getSessionSlotDuration(sessionState),
  };
};

export const createUpdatedAppointmentSessionObject = (sessionState, existingAppointment) => {
    const updated = {};

    // if (existingAppointment.hospitalId) {
    //     updated.hospitalId = existingAppointment.hospitalId;
    // }

    // if (existingAppointment.wardId) {
    //     updated.wardId = existingAppointment.wardId;
    // }

    if (sessionState?.locked) {
        updated.status = 'LOCKED';
    }

    if (!sessionState?.active) {
        updated.status = 'CANCELLED';

        return updated;
    }

    if (!moment(sessionState.endDate).isSame(moment(existingAppointment.endDate), 'date')) {
        updated.endDate = moment(sessionState.endDate)
            .set({
                hours: sessionState.endTime.hours(),
                minutes: sessionState.endTime.minutes(),
                seconds: 0,
                miliseconds: 0,
            }).toDate();
        updated.rrule = modifyRruleString({...existingAppointment, endDate: updated.endDate});
    }

    if (sessionState?.sessionName !== existingAppointment.sessionName) {
        updated.sessionName = sessionState?.sessionName;
    }

    if (sessionState?.practitioner !== existingAppointment.practitioner) {
        updated.practitioner = sessionState?.practitioner;
    }

    if (sessionState?.wardId !== existingAppointment.wardId) {
        updated.wardId = sessionState?.wardId;
    }

    return updated;
};

export const bulkCreateAppointmentSessionObjects = (sessionObj) => {
  return Object.keys(sessionObj?.customSessionTime).map((customSessionTimeKey) => {
      const dayOfWeekCustomTime = sessionObj?.customSessionTime?.[customSessionTimeKey];

    const newSessionObj = {
        ...sessionObj,
      startDate: moment(sessionObj.startDate)
        .set({
          hours: moment(dayOfWeekCustomTime.startTime).hours(),
          minutes: moment(dayOfWeekCustomTime.startTime).minutes(),
          seconds: 0,
          miliseconds: 0,
        }).toDate(),
      endDate: moment(sessionObj.endDate)
        .set({
          hours: moment(dayOfWeekCustomTime.endTime).hours(),
          minutes: moment(dayOfWeekCustomTime.endTime).minutes(),
          seconds: 0,
          miliseconds: 0,
        }).toDate(),
      wardId: sessionObj.wardId,
      hospitalId: sessionObj.hospitalId,
      sessionName: sessionObj.sessionName.trim() ?? "",
      clinicsId: sessionObj.clinicsId ?? "",
      practitioner: sessionObj.practitioner ?? "",
      practitionerRole: sessionObj.practitionerRole ?? "",
      assignedSpecialty: sessionObj.assignedSpecialty ?? "",
      status: getAppointmentSessionStatus(sessionObj),
      daysOfWeek: [customSessionTimeKey],
      slotStartTime: moment(dayOfWeekCustomTime.startTime).format("HH:mm"),
    };

    newSessionObj.rrule = getIntervalRrule(newSessionObj).toString();
    newSessionObj.slotDurationInMinutes = getSessionSlotDuration(newSessionObj);

    return cleanSessionObj(newSessionObj);
  });
};

export const groupClinicianFilter = (sessions, keyPath, getId, getDescription, getValue, fallback, clinicianDict, selectedDay, activeFilters) => {
    const clinicianMap = new Map();
    const clinicianLookup = new Map();

    // 1. Populate lookup from dictionary
    if (clinicianDict) {
        clinicianDict.forEach(c => {
            if (c && c.__id) clinicianLookup.set(String(c.__id), c);
        });
    }

    // 2. Populate lookup from sessions (backup for missing dictionary entries)
    // also build the map of IDs to process
    const sessionIdsToProcess = new Map(); // Map<Session, Set<ClinicianID>>

    forEach(sessions, sess => {
        if (!sess || typeof sess !== 'object') return;

        // Backup lookup: if session has a full consultant object, add it
        if (sess.consultant && sess.consultant.__id) {
            const cId = String(sess.consultant.__id);
            if (!clinicianLookup.has(cId)) {
                clinicianLookup.set(cId, sess.consultant);
            }
        }

        const ids = new Set();
        const { effectiveClinician } = sess;

        if (selectedDay) {
            // Swimlane: Prioritize effectiveClinician[selectedDay]
            let chosenId = null;
            if (effectiveClinician && typeof effectiveClinician === 'object') {
                const v = effectiveClinician[selectedDay];
                if (typeof v === 'string' && v.trim() !== '') chosenId = v;
            }

            if (!chosenId && typeof sess.practitioner === 'string' && sess.practitioner.trim() !== '') {
                chosenId = sess.practitioner;
            }

            ids.add(chosenId);
        } else {
            // Calendar: Include all involved clinicians
            if (typeof sess.practitioner === 'string' && sess.practitioner.trim() !== '') {
                ids.add(sess.practitioner);
            }

            if (effectiveClinician) {
                if (typeof effectiveClinician === 'string' && effectiveClinician.trim() !== '') {
                    ids.add(effectiveClinician);
                } else if (Array.isArray(effectiveClinician)) {
                    forEach(effectiveClinician, item => {
                        if (typeof item === 'string' && item.trim() !== '') ids.add(item);
                    });
                } else if (typeof effectiveClinician === 'object') {
                    forEach(Object.values(effectiveClinician), val => {
                        if (typeof val === 'string' && val.trim() !== '') ids.add(val);
                    });
                }
            }

            if (ids.size === 0) ids.add(null);
        }

        sessionIdsToProcess.set(sess, ids);
    });

    // 3. Grouping Logic
    const addSessionToGroup = (clinicianId, sess) => {
        let targetId = clinicianId;
        let isFallback = false;

        if (!targetId) {
            targetId = fallback.__id;
            isFallback = true;
        }

        const mapKey = String(targetId);

        if (!clinicianMap.has(mapKey)) {
            const clinician = clinicianLookup.get(mapKey);

            let description = 'Not provided';
            if (isFallback) {
                description = fallback.__description;
            } else if (clinician) {
                // Try callback first
                const cbDesc = getDescription(clinician);
                if (cbDesc && cbDesc.trim() !== '') {
                    description = cbDesc;
                } else {
                    // Fallback to property
                    description = clinician.__description || targetId;
                }
            } else {
                // If not found in dict or session backup
                description = targetId || 'Not provided';
            }

            const value = isFallback ? fallback.value : targetId;

            const newGroup = {
                __id: targetId,
                __description: description,
                value: value,
                disabled: false, // Ensure it's enabled so user can interact
                sess: sess ? [sess] : [],
            };
            clinicianMap.set(mapKey, newGroup);
        } else {
            const entry = clinicianMap.get(mapKey);
            if (sess) entry.sess.push(sess);
        }
    };

    sessionIdsToProcess.forEach((ids, sess) => {
        ids.forEach(id => addSessionToGroup(id, sess));
    });

    // 4. Ensure active filters are present
    if (activeFilters && Array.isArray(activeFilters)) {
        activeFilters.forEach(filter => {
            const filterId = filter.value ? filter.value : filter.__id; // Handle logic from groupFilters/UI
            // Note: filter objects usually have __id and value. UI uses value or __id depending on structure.
            // In groupFilters fallback result: { value: null } -> ID 'noVal'
            
            // Check if we need to map a special value
            let targetId = filterId;
            if (targetId === fallback.value || targetId === fallback.__id || targetId === null) {
                 // The 'unassigned' logic usually uses 'noVal' as ID in previous logic, but here we use mapKey.
                 // If the filter has value=null, it matches the fallback.
                 if (targetId === fallback.value) targetId = fallback.__id;
            }
            
            // Filter might be the full object structure { __id: ..., value: ... }
            if (filter.value !== undefined) targetId = filter.value;
            
            // If the filter represents "Not assigned" (null), add null to group
            if (targetId === null) targetId = null;

            addSessionToGroup(targetId, null); // Pass null session to indicate it's forced
        });
    }

    return Array.from(clinicianMap.values()).sort((a, b) => {
        const ad = (a.__description || '').toString().toLowerCase();
        const bd = (b.__description || '').toString().toLowerCase();
        if (ad < bd) return -1;
        if (ad > bd) return 1;
        return 0;
    });
};